import { Button, Flex, Tooltip } from 'antd'
import { createStyles } from 'antd-style'
import type { ReactNode } from 'react'

export type AuthMethodButton = {
  id: string
  /** Full, translated label — used as the accessible name (aria-label). */
  label: string
  /** Short visible tile label (e.g. "Google"). Falls back to `label`. */
  shortLabel?: string
  icon: ReactNode
  loading: boolean
  disabled: boolean
  onClick: () => void
  testId?: string
}

const useStyles = createStyles(({ token }) => ({
  // Vertical sign-in tile: a round 40px icon badge above a short label. The
  // button surface stays flat — only the circular badge reads as filled — so
  // it matches the auth card in both light and dark themes.
  tile: {
    height: 'auto',
    minWidth: 64,
    flexDirection: 'column',
    gap: 6,
    paddingBlock: 8,
    paddingInline: 8,

    // Keep the button itself flat; the round badge is the only filled surface.
    '&:hover, &:active': {
      background: 'transparent !important'
    },

    // 40px circular badge around the brand mark.
    '.ant-btn-icon': {
      width: 40,
      height: 40,
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginInlineEnd: 0,
      lineHeight: 0,
      background: token.colorFillSecondary,
      transition: `background ${token.motionDurationMid} ${token.motionEaseInOut}`
    },
    // Brand SVGs render at their native 18px, centred inside the 40px badge.
    '.ant-btn-icon svg': {
      width: 18,
      height: 18
    },
    '&:hover .ant-btn-icon': {
      background: token.colorFill
    }
  }
}))

type Props = {
  methods: AuthMethodButton[]
}

/**
 * Social / passwordless sign-in methods, rendered as a centred row of
 * icon-over-label tiles. The brand mark stacks above a short label so each
 * provider stays recognisable; a single method renders as one centred tile.
 */
export const SocialMethodButtons = ({ methods }: Props) => {
  const { styles } = useStyles()

  if (methods.length === 0) {
    return null
  }

  return (
    <Flex
      justify="center"
      gap={12}
      wrap="wrap"
      data-testid="login-social-methods"
    >
      {methods.map((method) => (
        <Tooltip key={method.id} title={method.label}>
          <Button
            type="text"
            className={styles.tile}
            icon={method.icon}
            loading={method.loading}
            disabled={method.disabled}
            onClick={method.onClick}
            aria-label={method.label}
            data-testid={method.testId}
          >
            {method.shortLabel ?? method.label}
          </Button>
        </Tooltip>
      ))}
    </Flex>
  )
}
